<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("admin_auto_order.sorting_fights") }}
        </template>
        <template v-slot:body>
            <template v-if="initDone">
                <div class="mb-5">
                    <div class="field">
                        <label class="label">{{ translate("admin_auto_order.sorting_type_2") }}</label>
                        <div class="control">
                            <div class="select">
                                <select v-model="sortingType">
                                    <option :value="TYPE_ENSURE_BREAK">
                                        {{ translate("admin_auto_order.automatic_sort") }}
                                    </option>
                                    <option :value="TYPE_ROUND_BASED">
                                        {{ translate("admin_auto_order.round_based") }}
                                    </option>
                                    <option :value="TYPE_ROUND_BASED_BEGIN_CATEGORIES_AT_THE_SAME_TIME">
                                        {{ translate("admin_auto_order.round_based_begin_all") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="field" :class="{ 'has-error': !isValidNumberOfFightsForBreak }" v-if="sortingType === TYPE_ENSURE_BREAK">
                        <label class="label">{{ translate("admin_auto_order.try_to_ensure_break") }}</label>
                        <div class="control">
                            <input type="number" min="1" max="6" class="input" v-model="numberOfFightsForBreak" />
                        </div>
                        <p class="help" v-if="!isValidNumberOfFightsForBreak">
                            {{ translate("admin_auto_order.please_provide_valid_number_of_fights") }}
                        </p>
                    </div>
                </div>

                <h5 class="is-size-5 has-border">{{ translate("admin_auto_order.schedules") }}</h5>

                <div class="box" v-for="schedule in autoOrderingSummary" :key="schedule.id">
                    <div class="columns is-vcentered is-multiline">
                        <div class="column is-12">
                            <p class="title is-4">{{ schedule.scheduleName }}</p>
                        </div>
                        <div class="column is-12">
                            <div class="buttons are-small">
                                <LoadingButton
                                    class="button is-primary"
                                    :disabled="isAnyMatOrderedInSchedule(schedule.id)"
                                    @click="autoOrderWholeSchedule(schedule)"
                                    :is-loading="scheduleSortingInProgress[schedule.id]"
                                >
                                    {{ translate("admin_auto_order.sort_schedule") }}
                                </LoadingButton>

                                <button class="button is-info" @click="toggleMatForSchedule(schedule.id)">
                                    <span>{{ translate("admin_auto_order.show_mats") }}</span>
                                    <span class="icon">
                                        <i v-if="showingScheduleMats[schedule.id]" class="fa fa-caret-up" />
                                        <i v-else class="fa fa-caret-down" />
                                    </span>
                                </button>
                                <LoadingButton
                                    class="button"
                                    @click="resetWholeSchedule(schedule)"
                                    :is-loading="scheduleResettingInProgress[schedule.id]"
                                >
                                    {{ translate("admin_auto_order.reset_sorting") }}
                                </LoadingButton>
                            </div>
                        </div>
                    </div>

                    <template v-if="!!showingScheduleMats[schedule.id]">
                        <div class="columns is-vcentered" v-for="matState in schedule.matStates" :key="matState.id">
                            <div class="column is-half">
                                <p class="title is-5" :class="getClassForState(matState.state)">
                                    {{ matState.matName }}
                                </p>
                                <p class="subtitle is-6" :class="getClassForState(matState.state)">
                                    {{ translate("admin_auto_order.sorting_type") }} {{ stateName(matState.state) }}
                                </p>
                            </div>
                            <div class="column">
                                <div class="buttons are-small">
                                    <LoadingButton
                                        v-if="matState.state === TYPE_RESET"
                                        class="button"
                                        @click="autoOrderSingleMat(schedule.id, matState.id)"
                                        :is-loading="matSortingInProgress[`${schedule.id}${matState.id}`]"
                                    >
                                        {{ translate("admin_auto_order.sort_mat") }}
                                    </LoadingButton>

                                    <LoadingButton
                                        v-else
                                        class="button is-small"
                                        @click="resetOrderingSingleMat(schedule.id, matState.id)"
                                        :is-loading="matResettingInProgress[`${schedule.id}${matState.id}`]"
                                    >
                                        {{ translate("admin_auto_order.reset_sorting") }}
                                    </LoadingButton>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <template v-else>
                <div class="columns is-centered">
                    <LoadingSpinner2 class="has-added-margin" />
                </div>
            </template>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.close") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script lang="ts" setup>
    import { ref, computed, watch } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import store from "../Common/store";
    import autoOrderingApi, { MatAutOrderingState, RequestedMat, ScheduleAutoOrderingState, SortingType } from "../Api/autoOrderingApi";
    import { successToast } from "../Common/toasts";
    import { useModal } from "../Common/useModal";

    const TYPE_RESET = 0;
    const TYPE_ENSURE_BREAK = 1;
    const TYPE_MANUAL_SORT = 2;
    const TYPE_ROUND_BASED = 3;
    const TYPE_ROUND_BASED_BEGIN_CATEGORIES_AT_THE_SAME_TIME = 4;

    const autoOrderingSummary = ref<ScheduleAutoOrderingState[]>([]);
    const showingScheduleMats = ref<Record<string, boolean>>({});
    const initDone = ref(false);
    const scheduleSortingInProgress = ref<Record<string, boolean>>({});
    const scheduleResettingInProgress = ref<Record<string, boolean>>({});
    const matSortingInProgress = ref<Record<string, boolean>>({});
    const matResettingInProgress = ref<Record<string, boolean>>({});
    const numberOfFightsForBreak = ref<number>(2);
    const sortingType = ref<SortingType>(TYPE_ENSURE_BREAK);

    const isValidNumberOfFightsForBreak = computed(() => {
        const number = Number(numberOfFightsForBreak.value);
        return number >= 1 && number <= 6;
    });

    const props = defineProps<{
        eventId: number;
    }>();

    watch(numberOfFightsForBreak, () => {
        if (isValidNumberOfFightsForBreak.value) {
            store.setInt(`auto_order_number_of_fights_for_break_${props.eventId}`, Number(numberOfFightsForBreak.value));
        }
    });

    function getClassForState(state: number): string {
        return state > 0 ? "has-text-success" : "has-text-danger";
    }

    function fetchAutoOrderingSummary(): Promise<void> {
        return autoOrderingApi
            .getAutoOrderingStateSummary(props.eventId)
            .then(summary => {
                autoOrderingSummary.value = summary;
            })
            .catch(handleError);
    }

    function autoOrderWholeSchedule(schedule: ScheduleAutoOrderingState): Promise<void> {
        scheduleSortingInProgress.value[schedule.id] = true;
        const targets = schedule.matStates
            .filter((matState: MatAutOrderingState) => matState.state === 0)
            .map((matState: MatAutOrderingState) => ({
                scheduleId: schedule.id,
                matId: matState.id,
            }));

        return sendAutoOrderRequest(sortingType.value, targets).finally(() => {
            scheduleSortingInProgress.value[schedule.id] = false;
        });
    }

    function autoOrderSingleMat(scheduleId: number, matId: number): Promise<void> {
        matSortingInProgress.value[`${scheduleId}${matId}`] = true;
        return sendAutoOrderRequest(sortingType.value, [{ scheduleId, matId }]).finally(() => {
            matSortingInProgress.value[`${scheduleId}${matId}`] = false;
        });
    }

    function resetWholeSchedule(schedule: ScheduleAutoOrderingState): Promise<void> {
        scheduleResettingInProgress.value[schedule.id] = true;

        const targets = schedule.matStates
            .filter((matState: MatAutOrderingState) => matState.state > 0)
            .map((matState: MatAutOrderingState) => ({
                scheduleId: schedule.id,
                matId: matState.id,
            }));

        return sendAutoOrderRequest(TYPE_RESET, targets).finally(() => {
            scheduleResettingInProgress.value[schedule.id] = false;
        });
    }

    function resetOrderingSingleMat(scheduleId: number, matId: number): Promise<void> {
        matResettingInProgress.value[`${scheduleId}${matId}`] = true;
        return sendAutoOrderRequest(TYPE_RESET, [{ scheduleId, matId }]).finally(() => {
            matResettingInProgress.value[`${scheduleId}${matId}`] = false;
        });
    }

    function sendAutoOrderRequest(type: SortingType, scheduleAndMats: RequestedMat[]): Promise<void> {
        if (!isValidNumberOfFightsForBreak.value) {
            alert(translate("admin_auto_order.please_provide_valid_number_of_fights"));
            return Promise.resolve();
        }

        if (type === TYPE_RESET && !confirm(translate("common.are_you_sure"))) {
            return Promise.resolve();
        }

        return autoOrderingApi
            .autoOrderFights(props.eventId, type, scheduleAndMats, numberOfFightsForBreak.value)
            .then(() => fetchAutoOrderingSummary())
            .then(() => {
                successToast(translate("admin_auto_order.fights_sorted"));
            })
            .catch(handleError);
    }

    function stateName(state: SortingType): string {
        if (state === TYPE_ENSURE_BREAK) {
            return translate("admin_auto_order.automatic_sort");
        }
        if (state === TYPE_MANUAL_SORT) {
            return translate("admin_auto_order.manual_sort");
        }
        if (state === TYPE_ROUND_BASED) {
            return translate("admin_auto_order.round_based");
        }

        return translate("admin_auto_order.no_sorting");
    }

    function toggleMatForSchedule(scheduleId: number): void {
        showingScheduleMats.value[scheduleId] = !showingScheduleMats.value[scheduleId];
    }

    const { open: openModal, close, modal } = useModal();

    function open(): Promise<void> {
        openModal();
        initDone.value = false;
        showingScheduleMats.value = {};
        numberOfFightsForBreak.value = Number(store.getInt(`auto_order_number_of_fights_for_break_${props.eventId}`) || 2);

        return fetchAutoOrderingSummary()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function isAnyMatOrderedInSchedule(scheduleId: number): boolean {
        const foundSchedule = autoOrderingSummary.value.find((schedule: ScheduleAutoOrderingState) => schedule.id === scheduleId);
        if (!foundSchedule) {
            return false;
        }

        return foundSchedule.matStates.some((matState: MatAutOrderingState) => matState.state > TYPE_RESET);
    }

    defineExpose({ open });
</script>

<style scoped>
    .box:last-child {
        margin-bottom: 1.5rem;
    }
</style>
